/* Finding room on the technician whose turn it is, for a booked client.
 *
 * Under the turn-order setting the next technician up gets an unrequested
 * booking, and the book is brought into line. Often that is one change: nothing
 * is in the way, and the move is clean. When something is, the desk is offered
 * Swap, which moves what is in the way first. `planRoom` is the answer to "what
 * would that take?", and the same answer drives both the item's wording and the
 * moves that run when Swap is tapped, so what the desk reads is what happens.
 *
 * Why the limit. A swap moves at most two other bookings besides the original,
 * whether both are in the way on the receiver or the first needs the second
 * moved to make room for it. Kevin's "two deep". A pair of services in one
 * square counts as the original, however many services it holds, because it is
 * one client in one chair. Past two, the plan is not something a person can read
 * on a row and vouch for, so the row says so and the desk fixes it in Mangomint.
 *
 * Why a requested booking is a wall. A client who asked for somebody by name was
 * promised that person. The same goes for a booking the caller marks
 * `movable: false`: the proxy cannot edit it, so a plan through it would stop
 * halfway. Neither is ever moved to make room, and if one is what stops the plan,
 * the row says that rather than just "no".
 *
 * Why the search is breadth first. Fewer moves are always the better plan, since
 * each one is another client's book changed for somebody else's turn. Looking at
 * every one-move plan before any two-move plan makes that automatic, and trying
 * receivers in `staffIds` order makes ties come out the same every time. There
 * is nothing random here. A suggestion that changes on a second look is on the
 * design's list of failures even if both answers work.
 *
 * Who may receive. Each receiver must be `clear` and `may` for the booking it
 * gets, and must not overlap anything that has arrived on it in this plan, the
 * unit included. A booking of its own in the way is not the end of it, since that
 * one can go too, within the limit. Nothing is moved twice, and nothing "moves"
 * to where it already is, so the search has to end.
 *
 * The giver's book is counted with the unit still on it. The unit moves last, so
 * anything landing on the giver lands while the unit is there, and the proxy's
 * clash check would refuse it. A booking may go to the giver only if it clears the
 * unit and everything else they hold. So two technicians cannot simply trade a
 * booking for the unit: it goes to somebody else, or the row says stuck.
 *
 * Why order matters. Mangomint does not stop a double-booking, so a plan that is
 * fine once finished can still overlap on the way there. Moves run far end first
 * and the original last, each landing in time already open. If two others would
 * each land on the other, as in m to T while T's j goes to m's old place, neither
 * can go first, and that plan is not offered even though its end state is clean:
 * it would be a double-booking in a real book however briefly. Nothing overlaps
 * along the way, with no exceptions.
 *
 * What the caller must see to. This file looks at the rows it is given and asks
 * nothing else. The unit's own locks are the caller's job: planRoom does not check
 * whether the unit itself is requested or editable, and only decides where it can
 * go. Rows from `bookOf` must carry `requested` and `movable`, since a row without
 * them reads as free to move. And half of another client's pair is not free to
 * move alone, so a caller should mark it `movable: false`.
 *
 * Pure, and imports nothing, so the same file runs on the board and in the edge
 * function. Everything only the store can answer arrives as a function.
 *
 *   unit        [{ id, from, to }] the services moving together.
 *   toStaffId   who they are going to. fromStaffId: who they are leaving.
 *   bookOf      (staffId) -> [{ id, from, to, requested, movable }]
 *   clear       (staffId, block) -> on shift, clear of time blocks and running
 *               squares. Not bookings: this file works those out.
 *   may         (staffId, block) -> mayTakeBooked.
 *   staffIds    in the order the desk would reach for them. The giver is tried
 *               after them if not listed, since `clear` and `may` still decide.
 *   maxOthers   how many others may move. Defaults to 2.
 *
 * Returns { kind: 'clean' | 'swap' | 'stuck', moves: [{ id, toStaffId }], why }
 * with `moves` in the order to run. A stuck answer has no moves, a `why` the desk
 * can read, and a stable `reason`:
 * 'receiver' | 'requested' | 'unmovable' | 'too_many' | 'no_receiver'.
 * It also has `blocking`, the ids of the bookings in the way, nearest first, on
 * the path that got furthest toward that reason, so the caller can name them.
 * First found wins a tie. Empty when the receiver is the problem.
 */

function overlaps(a, b) { return a.from < b.to && a.to > b.from; }

function byStart(a, b) {
  if (a.from !== b.from) return a.from - b.from;
  return String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0;
}

function hitsAny(block, others) {
  return others.some(function (o) { return overlaps(block, o); });
}

function stuck(reason, why, blocking) {
  return { kind: 'stuck', moves: [], why: why, reason: reason, blocking: blocking };
}

function tooMany(max, blocking) {
  return stuck('too_many', 'This would take more than ' + max + ' other booking' + (max === 1 ? '' : 's') +
    ' moved, so it has to be fixed in Mangomint.', blocking);
}
function requestedInWay(blocking) {
  return stuck('requested', 'A booking in the way was asked for by name, so it stays where it is.', blocking);
}
function unmovableInWay(blocking) {
  return stuck('unmovable', 'A booking in the way can\'t be moved from here, so it has to be fixed in Mangomint.', blocking);
}

function idsOf(bookings) {
  return bookings.map(function (b) { return b.id; });
}

/* The moves in an order where each lands in open time: a move waits for anything
 * leaving its destination that it overlaps. Among those ready, the order they
 * were found in, which keeps the answer deterministic. Null when nothing can go
 * first. The unit is not in here: it runs last, by rule. */
function runOrder(moved) {
  var left = moved.slice();
  var ordered = [];
  while (left.length) {
    var ready = -1;
    for (var i = 0; i < left.length && ready === -1; i++) {
      var waiting = left.some(function (other) {
        return other !== left[i] && other.from === left[i].to && overlaps(other.booking, left[i].booking);
      });
      if (!waiting) ready = i;
    }
    if (ready === -1) return null;
    ordered.push(left.splice(ready, 1)[0]);
  }
  return ordered;
}

export function planRoom(args) {
  var unit = args.unit;
  var toStaffId = args.toStaffId;
  var max = args.maxOthers == null ? 2 : args.maxOthers;
  var clear = args.clear;
  var may = args.may;

  // A receiver who cannot have the client is the end of it, whatever else moves.
  for (var u = 0; u < unit.length; u++) {
    if (!clear(toStaffId, unit[u]) || !may(toStaffId, unit[u])) {
      return stuck('receiver', 'That technician can\'t take this booking: they are off, on a break, ' +
        'with a client, or not set up for the service.', []);
    }
  }

  var unitIds = unit.map(function (s) { return s.id; });
  function book(staffId) {
    return (args.bookOf(staffId) || []).filter(function (b) {
      return unitIds.indexOf(b.id) === -1;
    }).sort(byStart);
  }
  var unitMoves = unit.map(function (s) { return { id: s.id, toStaffId: toStaffId }; });

  // What is in the way on the receiver has no way round: it must all go. A plan
  // that fails on one of these fails on every branch, so say so now.
  var inWay = book(toStaffId).filter(function (b) { return hitsAny(b, unit); });
  if (inWay.some(function (b) { return b.requested; })) return requestedInWay(idsOf(inWay));
  if (inWay.some(function (b) { return b.movable === false; })) return unmovableInWay(idsOf(inWay));
  if (inWay.length > max) return tooMany(max, idsOf(inWay));

  var receivers = (args.staffIds || []).slice();
  if (args.fromStaffId != null && receivers.indexOf(args.fromStaffId) === -1) {
    receivers.push(args.fromStaffId);
  }

  // What stopped the branches that died, to give the desk the real reason: for
  // each kind of dead end, the bookings in the way on the longest path to it.
  var hit = {};
  function died(kind, ids) {
    if (!hit[kind] || ids.length > hit[kind].length) hit[kind] = ids;
  }

  // The unit counts on both ends: on the giver until it moves, which is last,
  // and on the receiver as already promised.
  function heldBy(moved, staffId) {
    var arrived = moved.filter(function (m) { return m.to === staffId; })
      .map(function (m) { return m.booking; });
    return staffId === toStaffId || staffId === args.fromStaffId ? arrived.concat(unit) : arrived;
  }

  /* A plan is who has moved so far and who still has to, the far side of a move
   * being what it found in the way. Each step settles one booking, so a level of
   * the queue is one more move, and the first finished plan has the fewest. */
  var queue = [{
    moved: [],
    pending: inWay.map(function (b) { return { booking: b, from: toStaffId }; })
  }];

  while (queue.length) {
    var plan = queue.shift();

    if (!plan.pending.length) {
      var ordered = runOrder(plan.moved);
      if (ordered) {
        return {
          kind: ordered.length ? 'swap' : 'clean',
          moves: ordered.map(function (m) { return { id: m.booking.id, toStaffId: m.to }; }).concat(unitMoves),
          why: ''
        };
      }
      died('circle', idsOf(plan.moved.map(function (m) { return m.booking; })));
      continue;
    }

    var next = plan.pending[0];
    var rest = plan.pending.slice(1);
    var settled = plan.moved.map(function (m) { return m.booking.id; })
      .concat(plan.pending.map(function (p) { return p.booking.id; }));
    var went = false;

    for (var r = 0; r < receivers.length; r++) {
      var staffId = receivers[r];
      if (staffId === next.from) continue;
      if (!may(staffId, next.booking) || !clear(staffId, next.booking)) continue;
      if (hitsAny(next.booking, heldBy(plan.moved, staffId))) continue;

      var alsoInWay = book(staffId).filter(function (b) {
        return settled.indexOf(b.id) === -1 && overlaps(b, next.booking);
      });
      var path = settled.concat(idsOf(alsoInWay));
      if (alsoInWay.some(function (b) { return b.requested; })) { died('requested', path); continue; }
      if (alsoInWay.some(function (b) { return b.movable === false; })) { died('unmovable', path); continue; }
      if (plan.moved.length + 1 + rest.length + alsoInWay.length > max) { died('tooMany', path); continue; }

      went = true;
      queue.push({
        moved: plan.moved.concat([{ booking: next.booking, from: next.from, to: staffId }]),
        pending: rest.concat(alsoInWay.map(function (b) { return { booking: b, from: staffId }; }))
      });
    }
    if (!went) died('nobody', settled);
  }

  if (hit.tooMany) return tooMany(max, hit.tooMany);
  if (hit.requested) return requestedInWay(hit.requested);
  if (hit.unmovable) return unmovableInWay(hit.unmovable);
  if (hit.circle) {
    return stuck('no_receiver', 'The bookings in the way would have to trade places at once and neither ' +
      'can go first, so it has to be fixed in Mangomint.', hit.circle);
  }
  return stuck('no_receiver', 'Nobody else is free and able to take the booking in the way.', hit.nobody);
}
